// LoadDemoModal — Stage 10 dataset picker. Opened from the TopBar "Load Demo"
// link. Lists every entry in `demoDatasets` by name + description; selecting
// one calls `loadDemoDataset`, navigates to Results, and closes the modal.

import { useStore } from '../../store/selectors';
import { demoDatasets, type DemoDataset } from '@backsolve/shared-logic/demoDatasets';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function LoadDemoModal({ open, onClose }: Props) {
  const loadDemoDataset = useStore((s) => s.loadDemoDataset);
  const pushToast = useStore((s) => s.pushToast);

  if (!open) return null;

  const handleSelect = (dataset: DemoDataset) => {
    try {
      loadDemoDataset(dataset);
      pushToast('success', `Loaded "${dataset.name}".`);
    } catch {
      pushToast('error', `Failed to load "${dataset.name}".`);
    }
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      style={modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="load-demo-title"
      onClick={onClose}
    >
      <div
        className="modal"
        style={modalBox}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="load-demo-title" className="screen-title" style={{ marginTop: 0 }}>
          Load Demo Dataset
        </h2>
        <p style={{ fontSize: 'var(--font-size-2)', color: 'var(--color-text-muted)' }}>
          Replace the current state with a realistic, pre-built 409A valuation.
          Loaded data is fully editable.
        </p>
        <ul style={listStyle}>
          {demoDatasets.map((d) => (
            <li key={d.id} style={itemStyle}>
              <button
                type="button"
                className="btn btn-secondary"
                style={buttonStyle}
                onClick={() => handleSelect(d)}
                aria-label={`Load ${d.name}`}
              >
                <strong>{d.name}</strong>
                <span style={descriptionStyle}>{d.description}</span>
              </button>
            </li>
          ))}
        </ul>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
};

const modalBox: React.CSSProperties = {
  background: 'var(--color-surface-raised)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-3)',
  padding: 'var(--space-5)',
  maxWidth: 640,
  width: 'min(92vw, 640px)',
  maxHeight: '86vh',
  overflowY: 'auto',
  boxShadow: 'var(--shadow-3)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const itemStyle: React.CSSProperties = { margin: 0 };

const buttonStyle: React.CSSProperties = {
  width: '100%',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
  padding: 'var(--space-3)',
};

const descriptionStyle: React.CSSProperties = {
  fontWeight: 'normal',
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-2)',
};
